/**
 * @module loggerService
 * @description Structured logging service for Preparo
 *
 * Provides a centralized, structured logging system with:
 * - Log levels (debug, info, warn, error)
 * - Context/metadata support
 * - Performance measurement utilities
 * - Log filtering based on environment
 * - Consistent formatting for production-ready logs
 *
 * @example
 * import { logger, createLogger } from './loggerService';
 *
 * // Global logger
 * logger.info('Application started');
 * logger.error('Failed to save', { docId: '123', error: err });
 *
 * // Scoped logger with context
 * const dbLogger = createLogger('IndexedDB');
 * dbLogger.info('Database initialized');
 * dbLogger.debug('Query executed', { store: 'projects', count: 5 });
 *
 * // Performance measurement
 * const timer = logger.startTimer('documentSave');
 * await saveDocument();
 * timer.end({ docId: '123' }); // Logs duration automatically
 */

import { dev } from "../config";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/** Log levels in order of severity */
export type LogLevel = "debug" | "info" | "warn" | "error";

/** Log entry structure */
export interface LogEntry {
  /** Timestamp in ISO 8601 format */
  timestamp: string;
  /** Log level */
  level: LogLevel;
  /** Logger context/scope (e.g., 'IndexedDB', 'Editor') */
  context: string;
  /** Log message */
  message: string;
  /** Additional metadata */
  meta?: Record<string, unknown>;
  /** Error object if applicable */
  error?: Error;
  /** Duration in ms for timed operations */
  duration?: number;
}

/** Timer interface for performance measurement */
export interface LogTimer {
  /** End the timer and log the duration */
  end: (meta?: Record<string, unknown>) => void;
  /** Get elapsed time without ending */
  elapsed: () => number;
  /** Cancel the timer without logging */
  cancel: () => void;
}

/** Logger configuration */
export interface LoggerConfig {
  /** Minimum log level to output */
  minLevel: LogLevel;
  /** Whether to include timestamps */
  includeTimestamp: boolean;
  /** Whether to include context prefix */
  includeContext: boolean;
  /** Custom log handler (for testing or external services) */
  handler?: (entry: LogEntry) => void;
  /** Whether logging is enabled */
  enabled: boolean;
}

/** Logger interface */
export interface Logger {
  debug: (message: string, meta?: Record<string, unknown>) => void;
  info: (message: string, meta?: Record<string, unknown>) => void;
  warn: (message: string, meta?: Record<string, unknown>) => void;
  error: (message: string, meta?: Record<string, unknown> | Error) => void;
  startTimer: (operation: string) => LogTimer;
  child: (context: string) => Logger;
  setLevel: (level: LogLevel) => void;
  setEnabled: (enabled: boolean) => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/** Log level priorities (higher = more severe) */
const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/** Console methods for each level */
const CONSOLE_METHODS: Record<LogLevel, "debug" | "info" | "warn" | "error"> = {
  debug: "debug",
  info: "info",
  warn: "warn",
  error: "error",
};

/** Level indicators for formatted output */
const LEVEL_INDICATORS: Record<LogLevel, string> = {
  debug: "[DEBUG]",
  info: "[INFO]",
  warn: "[WARN]",
  error: "[ERROR]",
};

/** Default configuration */
const DEFAULT_CONFIG: LoggerConfig = {
  minLevel: dev.verboseLogging ? "debug" : "info",
  includeTimestamp: true,
  includeContext: true,
  enabled: true,
};

// ============================================================================
// LOG HISTORY (for debugging/export)
// ============================================================================

/** Maximum number of log entries to keep in history */
const MAX_HISTORY_SIZE = 500;

/** In-memory log history */
const logHistory: LogEntry[] = [];

/**
 * Add entry to log history (circular buffer)
 */
function addToHistory(entry: LogEntry): void {
  logHistory.push(entry);
  if (logHistory.length > MAX_HISTORY_SIZE) {
    logHistory.shift();
  }
}

/**
 * Get log history (for debugging/export)
 */
export function getLogHistory(): LogEntry[] {
  return [...logHistory];
}

/**
 * Clear log history
 */
export function clearLogHistory(): void {
  logHistory.length = 0;
}

/**
 * Export log history as JSON string
 */
export function exportLogHistory(): string {
  return JSON.stringify(logHistory, null, 2);
}

// ============================================================================
// FORMATTING
// ============================================================================

/**
 * Format a log entry for console output
 */
function formatLogEntry(entry: LogEntry, config: LoggerConfig): string {
  const parts: string[] = [];

  // Timestamp
  if (config.includeTimestamp) {
    const time = new Date(entry.timestamp).toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      fractionalSecondDigits: 3,
    });
    parts.push(`[${time}]`);
  }

  // Level
  parts.push(LEVEL_INDICATORS[entry.level]);

  // Context
  if (config.includeContext && entry.context) {
    parts.push(`[${entry.context}]`);
  }

  // Message
  parts.push(entry.message);

  // Duration
  if (entry.duration !== undefined) {
    parts.push(`(${entry.duration.toFixed(2)}ms)`);
  }

  return parts.join(" ");
}

/**
 * Format metadata for console output
 */
function formatMeta(
  meta: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!meta || Object.keys(meta).length === 0) {
    return undefined;
  }

  // Clean up meta for display (limit string lengths, etc.)
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(meta)) {
    if (typeof value === "string" && value.length > 200) {
      cleaned[key] = value.substring(0, 200) + "...";
    } else if (value instanceof Error) {
      cleaned[key] = {
        name: value.name,
        message: value.message,
        stack: value.stack?.split("\n").slice(0, 5).join("\n"),
      };
    } else {
      cleaned[key] = value;
    }
  }
  return cleaned;
}

// ============================================================================
// LOGGER IMPLEMENTATION
// ============================================================================

/**
 * Create a logger instance with the given context and configuration
 */
function createLoggerInstance(context: string, config: LoggerConfig): Logger {
  /**
   * Check if a log level should be output
   */
  function shouldLog(level: LogLevel): boolean {
    if (!config.enabled) return false;
    return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[config.minLevel];
  }

  /**
   * Core logging function
   */
  function log(
    level: LogLevel,
    message: string,
    meta?: Record<string, unknown>,
  ): void {
    if (!shouldLog(level)) return;

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      context,
      message,
      meta,
    };

    // Add to history
    addToHistory(entry);

    // Custom handler
    if (config.handler) {
      config.handler(entry);
      return;
    }

    // Console output
    const formattedMessage = formatLogEntry(entry, config);
    const formattedMeta = formatMeta(meta);
    const consoleMethod = CONSOLE_METHODS[level];

    if (formattedMeta) {
      console[consoleMethod](formattedMessage, formattedMeta);
    } else {
      console[consoleMethod](formattedMessage);
    }
  }

  /**
   * Log an error with stack trace
   */
  function logError(
    message: string,
    metaOrError?: Record<string, unknown> | Error,
  ): void {
    let meta: Record<string, unknown> = {};
    let error: Error | undefined;

    if (metaOrError instanceof Error) {
      error = metaOrError;
      meta = { error: metaOrError };
    } else if (metaOrError) {
      meta = metaOrError;
      if (metaOrError.error instanceof Error) {
        error = metaOrError.error as Error;
      }
    }

    if (!shouldLog("error")) return;

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: "error",
      context,
      message,
      meta,
      error,
    };

    addToHistory(entry);

    if (config.handler) {
      config.handler(entry);
      return;
    }

    const formattedMessage = formatLogEntry(entry, config);
    if (error) {
      console.error(formattedMessage, error);
    } else {
      console.error(formattedMessage, formatMeta(meta) || "");
    }
  }

  /**
   * Start a performance timer
   */
  function startTimer(operation: string): LogTimer {
    const startTime = performance.now();
    let ended = false;

    return {
      end: (meta?: Record<string, unknown>) => {
        if (ended) return;
        ended = true;

        const duration = performance.now() - startTime;
        const entry: LogEntry = {
          timestamp: new Date().toISOString(),
          level: "debug",
          context,
          message: `${operation} completed`,
          meta,
          duration,
        };

        addToHistory(entry);

        if (!shouldLog("debug")) return;

        if (config.handler) {
          config.handler(entry);
          return;
        }

        const formattedMessage = formatLogEntry(entry, config);
        const formattedMeta = formatMeta(meta);

        if (formattedMeta) {
          console.debug(formattedMessage, formattedMeta);
        } else {
          console.debug(formattedMessage);
        }
      },
      elapsed: () => performance.now() - startTime,
      cancel: () => {
        ended = true;
      },
    };
  }

  /**
   * Create a child logger with additional context
   */
  function child(childContext: string): Logger {
    const newContext = context ? `${context}:${childContext}` : childContext;
    return createLoggerInstance(newContext, config);
  }

  /**
   * Update minimum log level
   */
  function setLevel(level: LogLevel): void {
    config.minLevel = level;
  }

  /**
   * Enable/disable logging
   */
  function setEnabled(enabled: boolean): void {
    config.enabled = enabled;
  }

  return {
    debug: (message, meta) => log("debug", message, meta),
    info: (message, meta) => log("info", message, meta),
    warn: (message, meta) => log("warn", message, meta),
    error: logError,
    startTimer,
    child,
    setLevel,
    setEnabled,
  };
}

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Create a scoped logger with custom context
 *
 * @param context - Logger context/scope name
 * @param options - Optional configuration overrides
 * @returns Logger instance
 *
 * @example
 * const dbLogger = createLogger('IndexedDB');
 * dbLogger.info('Database initialized');
 */
export function createLogger(
  context: string,
  options: Partial<LoggerConfig> = {},
): Logger {
  const config: LoggerConfig = {
    ...DEFAULT_CONFIG,
    ...options,
  };
  return createLoggerInstance(context, config);
}

/**
 * Global application logger
 */
export const logger = createLogger("App");

// ============================================================================
// PERFORMANCE MONITORING
// ============================================================================

/** Performance metrics storage */
interface PerformanceMetrics {
  operation: string;
  count: number;
  totalDuration: number;
  minDuration: number;
  maxDuration: number;
  avgDuration: number;
  lastDuration: number;
  lastTimestamp: string;
}

/** Metrics storage */
const metricsStore: Map<string, PerformanceMetrics> = new Map();

/**
 * Record a performance metric
 */
export function recordMetric(operation: string, duration: number): void {
  const existing = metricsStore.get(operation);

  if (existing) {
    existing.count += 1;
    existing.totalDuration += duration;
    existing.minDuration = Math.min(existing.minDuration, duration);
    existing.maxDuration = Math.max(existing.maxDuration, duration);
    existing.avgDuration = existing.totalDuration / existing.count;
    existing.lastDuration = duration;
    existing.lastTimestamp = new Date().toISOString();
  } else {
    metricsStore.set(operation, {
      operation,
      count: 1,
      totalDuration: duration,
      minDuration: duration,
      maxDuration: duration,
      avgDuration: duration,
      lastDuration: duration,
      lastTimestamp: new Date().toISOString(),
    });
  }
}

/**
 * Get all performance metrics
 */
export function getPerformanceMetrics(): PerformanceMetrics[] {
  return Array.from(metricsStore.values());
}

/**
 * Get metrics for a specific operation
 */
export function getMetric(operation: string): PerformanceMetrics | undefined {
  return metricsStore.get(operation);
}

/**
 * Clear all performance metrics
 */
export function clearMetrics(): void {
  metricsStore.clear();
}

/**
 * Create a performance-tracked function wrapper
 *
 * @param operation - Operation name for metrics
 * @param fn - Function to wrap
 * @returns Wrapped function that records performance
 *
 * @example
 * const trackedSave = withPerformanceTracking('documentSave', saveDocument);
 * await trackedSave(docId, content);
 */
export function withPerformanceTracking<
  T extends (...args: unknown[]) => Promise<unknown>,
>(operation: string, fn: T): T {
  return (async (...args: Parameters<T>) => {
    const start = performance.now();
    try {
      const result = await fn(...args);
      const duration = performance.now() - start;
      recordMetric(operation, duration);
      return result;
    } catch (error) {
      const duration = performance.now() - start;
      recordMetric(`${operation}:error`, duration);
      throw error;
    }
  }) as T;
}

// ============================================================================
// SPECIALIZED LOGGERS
// ============================================================================

/** Pre-configured loggers for common modules */
export const loggers = {
  db: createLogger("IndexedDB"),
  editor: createLogger("Editor"),
  store: createLogger("Store"),
  pandoc: createLogger("Pandoc"),
  ui: createLogger("UI"),
  network: createLogger("Network"),
  validation: createLogger("Validation"),
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  logger,
  createLogger,
  loggers,
  getLogHistory,
  clearLogHistory,
  exportLogHistory,
  recordMetric,
  getPerformanceMetrics,
  getMetric,
  clearMetrics,
  withPerformanceTracking,
};
